import React, { useState, useEffect } from 'react';
import { TrendingUp, Users, Link as LinkIcon, BarChart3, Calendar, Download, Filter } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { adminAPI } from '../../services/api';
import Card from '../common/Card';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import Table from '../common/Table';
import Badge from '../common/Badge';
import Button from '../common/Button';

const SystemAnalytics = () => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('30');
  const { executeApiCall } = useApi();

  useEffect(() => {
    fetchAnalyticsData();
  }, [selectedPeriod]);

  const fetchAnalyticsData = async () => {
    try {
      const result = await executeApiCall(
        () => adminAPI.getSystemAnalytics({ period: selectedPeriod })
      );
      
      if (result.success) {
        setAnalyticsData(result.data);
      }
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError('Failed to load system analytics data');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatGrowthRate = (rate) => {
    const isPositive = parseFloat(rate) >= 0;
    return (
      <span className={`flex items-center ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        <TrendingUp className={`h-4 w-4 mr-1 ${isPositive ? '' : 'transform rotate-180'}`} />
        {Math.abs(parseFloat(rate)).toFixed(1)}%
      </span>
    );
  };

  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'android':
        return '📱';
      case 'ios':
        return '📱';
      case 'desktop':
        return '💻';
      default:
        return '🌐';
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <Alert type="error" message={error} />;
  }

  if (!analyticsData) {
    return <Alert type="info" message="No analytics data available" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-ink-900">System Analytics</h1>
          <p className="text-ink-600 mt-2">Comprehensive system-wide analytics and insights</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <Button
            variant="outline"
            onClick={fetchAnalyticsData}
          >
            <Filter className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Growth Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-100 text-blue-600">
              <Users className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-600">User Growth</p>
              <p className="text-2xl font-bold text-ink-900">
                {formatNumber(analyticsData.growthMetrics.users.new)}
              </p>
              <div className="flex items-center mt-1">
                {formatGrowthRate(analyticsData.growthMetrics.users.growthRate)}
                <span className="text-xs text-ink-500 ml-2">vs total</span>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-100 text-green-600">
              <LinkIcon className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-600">Link Growth</p>
              <p className="text-2xl font-bold text-ink-900">
                {formatNumber(analyticsData.growthMetrics.links.new)}
              </p>
              <div className="flex items-center mt-1">
                {formatGrowthRate(analyticsData.growthMetrics.links.growthRate)}
                <span className="text-xs text-ink-500 ml-2">vs total</span>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-purple-100 text-purple-600">
              <BarChart3 className="h-6 w-6" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-ink-600">Click Growth</p>
              <p className="text-2xl font-bold text-ink-900">
                {formatNumber(analyticsData.growthMetrics.clicks.new)}
              </p>
              <div className="flex items-center mt-1">
                {formatGrowthRate(analyticsData.growthMetrics.clicks.growthRate)}
                <span className="text-xs text-ink-500 ml-2">vs total</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Top Performing Links */}
      <Card>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-ink-900">Top Performing Links</h3>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.Header>Link</Table.Header>
              <Table.Header>User</Table.Header>
              <Table.Header>Clicks</Table.Header>
              <Table.Header>Status</Table.Header>
              <Table.Header>Created</Table.Header>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {analyticsData.topLinks.map((link, index) => (
              <Table.Row key={link.id}>
                <Table.Cell>
                  <div className="max-w-xs truncate">
                    <p className="text-sm font-medium text-ink-900">{link.title || link.original_url}</p>
                    <p className="text-xs text-ink-500">{link.short_code}</p>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-ink-900">{link.user_username || 'Unknown'}</span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm font-medium text-ink-900">{formatNumber(link.clicks)}</span>
                </Table.Cell>
                <Table.Cell>
                  <Badge 
                    variant={link.is_active ? 'success' : 'secondary'}
                    text={link.is_active ? 'Active' : 'Inactive'}
                  />
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-ink-500">
                    {new Date(link.created_at).toLocaleDateString()}
                  </span>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Card>

      {/* Top Users */}
      <Card>
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-ink-900">Most Active Users</h3>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.Header>User</Table.Header>
              <Table.Header>Email</Table.Header>
              <Table.Header>Links</Table.Header>
              <Table.Header>Total Clicks</Table.Header>
              <Table.Header>Avg Clicks/Link</Table.Header>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {analyticsData.topUsers.map((user, index) => (
              <Table.Row key={user.id}>
                <Table.Cell>
                  <span className="text-sm font-medium text-ink-900">{user.username}</span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-ink-500">{user.email}</span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-ink-900">{user.link_count || 0}</span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm font-medium text-ink-900">{formatNumber(user.total_clicks || 0)}</span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-ink-500">
                    {user.link_count > 0 ? Math.round((user.total_clicks || 0) / user.link_count) : 0}
                  </span>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Card>

      {/* Link Trends */}
      <Card>
        <h3 className="text-lg font-semibold text-ink-900 mb-6">Link Creation Trends (Last {selectedPeriod} days)</h3>
        <div className="overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Header>Date</Table.Header>
                <Table.Header>Links Created</Table.Header>
                <Table.Header>Total Clicks</Table.Header>
                <Table.Header>Avg Clicks/Link</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {analyticsData.linkTrends.slice(0, 10).map((trend, index) => (
                <Table.Row key={trend.date}>
                  <Table.Cell>
                    <span className="text-sm font-medium text-ink-900">
                      {new Date(trend.date).toLocaleDateString()}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-sm text-ink-900">{formatNumber(trend.links_created)}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-sm text-ink-900">{formatNumber(trend.total_clicks)}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-sm text-ink-500">{trend.avg_clicks.toFixed(1)}</span>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      </Card>

      {/* Platform Trends */}
      <Card>
        <h3 className="text-lg font-semibold text-ink-900 mb-6">Platform Performance (Last {selectedPeriod} days)</h3>
        <div className="overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Header>Platform</Table.Header>
                <Table.Header>Total Clicks</Table.Header>
                <Table.Header>Unique Visitors</Table.Header>
                <Table.Header>This Week</Table.Header>
                <Table.Header>Today</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {analyticsData.platformTrends.map((platform, index) => (
                <Table.Row key={platform.platform}>
                  <Table.Cell>
                    <div className="flex items-center">
                      <span className="mr-2">
                        {platform.platform === 'android' ? '📱' : 
                         platform.platform === 'ios' ? '📱' : 
                         platform.platform === 'desktop' ? '💻' : '🌐'}
                      </span>
                      <span className="text-sm font-medium text-ink-900 capitalize">
                        {platform.platform || 'Unknown'}
                      </span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-sm text-ink-900">{formatNumber(platform.total_clicks)}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-sm text-ink-500">{formatNumber(platform.unique_visitors)}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-sm text-ink-900">{formatNumber(platform.clicks_week)}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-sm text-ink-500">{formatNumber(platform.clicks_today)}</span>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      </Card>

      {/* Daily Trends */}
      <Card>
        <h3 className="text-lg font-semibold text-ink-900 mb-6">Daily Performance Trends (Last {selectedPeriod} days)</h3>
        <div className="overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.Row>
                <Table.Header>Date</Table.Header>
                <Table.Header>Total Clicks</Table.Header>
                <Table.Header>Unique Visitors</Table.Header>
                <Table.Header>Active Links</Table.Header>
                <Table.Header>Android</Table.Header>
                <Table.Header>iOS</Table.Header>
                <Table.Header>Desktop</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {analyticsData.dailyTrends.slice(0, 10).map((trend, index) => (
                <Table.Row key={trend.date}>
                  <Table.Cell>
                    <span className="text-sm font-medium text-ink-900">
                      {new Date(trend.date).toLocaleDateString()}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-sm text-ink-900">{formatNumber(trend.total_clicks)}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-sm text-ink-500">{formatNumber(trend.unique_visitors)}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-sm text-ink-500">{formatNumber(trend.unique_links)}</span>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex items-center">
                      <span className="mr-2">📱</span>
                      <span className="text-sm text-ink-900">{formatNumber(trend.android_clicks)}</span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex items-center">
                      <span className="mr-2">📱</span>
                      <span className="text-sm text-ink-900">{formatNumber(trend.ios_clicks)}</span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex items-center">
                      <span className="mr-2">💻</span>
                      <span className="text-sm text-ink-900">{formatNumber(trend.desktop_clicks)}</span>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <div className="text-center">
            <p className="text-sm font-medium text-ink-600">Total Users</p>
            <p className="text-2xl font-bold text-ink-900">
              {formatNumber(analyticsData.growthMetrics.users.total)}
            </p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-sm font-medium text-ink-600">Total Links</p>
            <p className="text-2xl font-bold text-ink-900">
              {formatNumber(analyticsData.growthMetrics.links.total)}
            </p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-sm font-medium text-ink-600">Total Clicks</p>
            <p className="text-2xl font-bold text-ink-900">
              {formatNumber(analyticsData.growthMetrics.clicks.total)}
            </p>
          </div>
        </Card>
        <Card>
          <div className="text-center">
            <p className="text-sm font-medium text-ink-600">Period</p>
            <p className="text-2xl font-bold text-ink-900">
              {selectedPeriod} days
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default SystemAnalytics;
